/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Suspense, use, useCallback, useEffect, useMemo, useRef, useTransition } from 'react';
import { useAsyncError, useLocation, useRouteLoaderData } from 'react-router';
import type { loader as rootLoader } from '@/root';
import type { Route } from './+types/_app.category.$categoryId';
import type { ShopperProducts, ShopperSearch } from '@/scapi';
import { NormalizedApiError } from '@/lib/api/normalized-api-error';
import { fetchCategory } from '@/lib/api/categories.server';
import { fetchSearchProducts } from '@/lib/api/search.server';
import { getAllQueryParams, getQueryParam, PRODUCT_SEARCH_QUERY_PARAMS } from '@/lib/query-params';
import { getConfig, useConfig } from '@salesforce/storefront-next-runtime/config';
import { siteContext } from '@salesforce/storefront-next-runtime/site-context';
import CategoryBreadcrumbs from '@/components/category-breadcrumbs';
import CategoryPagination from '@/components/category-pagination';
import ActiveFilters from '@/components/category-refinements/active-filters';
import FiltersButton from '@/components/category-refinements/filters-button';
import CategoryRefinementsPanel from '@/components/category-refinements/panel';
import { usePanelState, useFiltersVisible } from '@/components/category-refinements/panel/use-panel-presentation';
import InvalidFilterCombination from '@/components/category-refinements/invalid-combination';
import { isOverFiltered } from '@/components/category-refinements/invalid-combination/is-over-filtered';
import CategorySorting from '@/components/category-sorting';
import CategoryViewToggle from '@/components/category-view-toggle';
import DeferredProductGrid from '@/components/product-grid';
import { useAnalytics } from '@/hooks/use-analytics';
import { PageType } from '@/lib/decorators/page-type';
import { RegionDefinition } from '@/lib/decorators/region-definition';
import { Region } from '@/components/region';
import { fetchPageWithComponentData } from '@/lib/page-designer/page-loader.server';
import CategoryBanner from '@/components/category-banner';
import CategoryBannerSkeleton from '@/components/category-banner/skeleton';
import { JsonLd } from '@/components/json-ld';
import { SeoMeta } from '@/components/seo-meta';
import { useTranslation } from 'react-i18next';
import { UITarget } from '@/targets/ui-target';
import { generateCategorySchema } from '@/utils/category-schema';
import { getPublicOrigin } from '@/utils/schema-url';
import { buildCanonicalUrl } from '@/utils/canonical-url';
import {
    getInitialFiltersOpen,
    getSearchWithoutFiltersParam,
    useFiltersPanelState,
} from '@/hooks/use-filters-panel-state';
import { useResultsGridLoading } from '@/hooks/use-results-grid-loading';
import { getLogger } from '@/lib/logger.server';
import { cn } from '@/lib/utils';
import type { ProductListingView } from '@/lib/product/view-preference';
import { readProductListingView } from '@/lib/product/view-preference.server';
import { useProductListingView } from '@/hooks/use-product-listing-view';

@PageType({
    name: 'Product Listing Page',
    description: 'Product listing page with product listings and personalized content',
    supportedAspectTypes: ['plp'],
})
@RegionDefinition([
    {
        id: 'plpTopFullWidth',
        name: 'Top Full Width Region',
        description: 'Full screen width region at the top of the results',
        maxComponents: 5,
    },
    {
        id: 'plpTopContent',
        name: 'Top Content Region',
        description: 'Content width region below sort/filter, above product grid',
        maxComponents: 5,
    },
    {
        id: 'plpBottom',
        name: 'Bottom Region',
        description: 'Region at the bottom of search results after product grid',
        maxComponents: 5,
    },
])
export class ProductListingPageMetadata {}

type CategoryPageData = {
    category: ShopperProducts.schemas['Category'];
    searchResultCritical: ShopperSearch.schemas['ProductSearchResult'];
    searchResultNonCritical: Promise<ShopperSearch.schemas['ProductSearchResult']>;
    page: ReturnType<typeof fetchPageWithComponentData>;
    categoryId: string;
    pageUrl: string;
    refine: string[];
    currency: string;
    locale: string;
    initialFiltersOpen?: boolean;
    categorySchema: Promise<ReturnType<typeof generateCategorySchema> | null>;
    /**
     * The shopper's grid/list preference, resolved from a cookie on the SERVER so the first
     * painted markup already matches it (CRCB2B-87). Deliberately not a URL parameter — see
     * `lib/product/view-preference.ts`.
     *
     * Keeping it out of the URL is what stops THIS route's loader re-running: `shouldRevalidate`
     * compares search params with the view parameter absent by construction. It does not stop
     * React Router revalidating other routes after the persist action — the ROOT loader had to be
     * opted out explicitly, in `lib/revalidation/routes/root.ts`. An earlier version of this
     * comment claimed changing the view "cannot trigger revalidation"; that was true of the
     * category loader only, and the root loader was re-running on every toggle.
     */
    view: ProductListingView;
};

/**
 * Server-side loader function that fetches category data and product search results.
 * This function runs on the server during SSR and prepares data for the category page.
 * @returns Object containing search results, category data, and page metadata
 */
export async function loader(args: Route.LoaderArgs): Promise<CategoryPageData> {
    const {
        context,
        request,
        params: { categoryId },
    } = args;
    const requestUrl = new URL(request.url);
    const { searchParams } = requestUrl;
    const logger = getLogger(context);
    logger.debug('Category: loader starting', {
        categoryId,
        offset: parseInt(searchParams.get('offset') || '0', 10),
    });
    const offset = parseInt(getQueryParam(searchParams, PRODUCT_SEARCH_QUERY_PARAMS.OFFSET) || '0', 10);
    const sort = getQueryParam(searchParams, PRODUCT_SEARCH_QUERY_PARAMS.SORT);
    const refine = getAllQueryParams(searchParams, PRODUCT_SEARCH_QUERY_PARAMS.REFINE);
    const initialFiltersOpen = getInitialFiltersOpen(searchParams);

    // Get currency and locale for cache-busting the page key
    const config = getConfig(context);
    const siteCtx = context.get(siteContext);
    if (!siteCtx) {
        logger.error('Category: site context is not available');
        throw new Response('Site context is not available', { status: 500 });
    }
    const { currency } = siteCtx;
    const locale = siteCtx.locale.id;
    const limit = config.search.products.hits.limit;

    let categoryData: ShopperProducts.schemas['Category'] | undefined;
    try {
        categoryData = await fetchCategory(context, categoryId, 1);
    } catch (e) {
        if (e instanceof NormalizedApiError && e.status) {
            throw new Response(e.message, { status: e.status });
        }
        throw new Response('Internal Server Error', { status: 500 });
    }

    // Keep non-category refinements and apply exactly one category refinement.
    // If URL already contains a cgid refine (e.g. from quick filters), honor it.
    // Otherwise, default to the category id from the route path.
    const effectiveRefine = refine.filter((r) => !r.startsWith('cgid='));
    const selectedCgidRefine = refine.find((r) => r.startsWith('cgid='));
    effectiveRefine.push(selectedCgidRefine ?? `cgid=${categoryId}`);

    // Ensure criticalCount doesn't exceed limit to prevent negative non-critical limit
    const criticalCount = config.search.products.hits.critical ?? 4;
    const safeCriticalCount = Math.min(criticalCount, limit);
    const searchResultCritical = await fetchSearchProducts(context, {
        limit: safeCriticalCount,
        offset,
        // Omit `sort` entirely when the URL carries none, rather than passing '' or undefined.
        // `fetchSearchProducts` builds its request by spreading these parameters over its own
        // defaults, and a spread copies explicitly-undefined values too — so both '' and
        // `undefined` would send an empty `sort` and override the site's configured rule with
        // nothing. Only an absent key leaves merchandising in charge of the resting order.
        //
        // Consequence worth knowing downstream: a request that sends no `sort` comes back with
        // no `selectedSortingOption` either, because the shopper has expressed no selection.
        // `CategorySorting` handles that by labelling the option the control actually displays.
        ...(sort ? { sort } : {}),
        refine: effectiveRefine,
        currency,
    });

    const effectiveCriticalCount = searchResultCritical.hits?.length ?? 0;
    const searchResultNonCritical = fetchSearchProducts(context, {
        limit: limit - effectiveCriticalCount,
        offset: offset + effectiveCriticalCount,
        ...(sort ? { sort } : {}),
        refine: effectiveRefine,
        currency,
    });

    const pageUrl = buildCanonicalUrl(requestUrl.origin, requestUrl.pathname, requestUrl.search);

    // Generate category schema in loader (server-side) for SEO
    const categorySchemaPromise = searchResultNonCritical
        .then((searchResult: ShopperSearch.schemas['ProductSearchResult']) => {
            try {
                // Use public origin from request headers instead of request.url
                // to avoid exposing internal AWS Lambda URLs in schema
                const publicOrigin = getPublicOrigin(request);
                const url = new URL(request.url);
                const schemaPageUrl = `${publicOrigin}${url.pathname}${url.search}`;
                // Validate inputs before generating schema
                if (!categoryData || !searchResult) {
                    return null;
                }
                return generateCategorySchema({
                    category: categoryData,
                    searchResult: {
                        ...searchResult,
                        hits: [...(searchResultCritical.hits || []), ...(searchResult.hits || [])],
                    },
                    config,
                    pageUrl: schemaPageUrl,
                    defaultCurrency: currency,
                });
            } catch (error) {
                logger.error('Error generating category schema in loader', {
                    error,
                });
                return null;
            }
        })
        .catch((error) => {
            logger.error('Error in category schema promise chain', {
                error,
            });
            return null;
        });

    return {
        category: categoryData,
        searchResultCritical,
        searchResultNonCritical,
        page: fetchPageWithComponentData(args, {
            aspectType: 'plp',
            categoryId,
        }),
        categoryId,
        pageUrl,
        refine: effectiveRefine,
        currency,
        locale,
        initialFiltersOpen,
        categorySchema: categorySchemaPromise,
        view: await readProductListingView(request, context),
    };
}

export { shouldRevalidate } from '@/lib/revalidation/routes/category';

/**
 * Category page component that displays a product category with filtering, sorting, and pagination.
 * This component uses the createPage factory to handle Suspense patterns.
 * @returns JSX element representing the category page
 */
function ProductGridError() {
    const rawError = useAsyncError();
    const error = rawError instanceof NormalizedApiError ? rawError : null;
    const { t } = useTranslation('common');
    return (
        <div role="alert" className="col-span-full py-8 text-center text-muted-foreground">
            <p>{t('productGrid.loadFailed')}</p>
            {import.meta.env.DEV && error && (
                <div className="mt-2 text-xs font-mono text-muted-foreground/70">
                    {error.status && <span>{error.status} </span>}
                    {error.message && <p>{error.message}</p>}
                </div>
            )}
        </div>
    );
}

/**
 * Component that renders JSON-LD schema when categorySchema promise resolves.
 * Must be inside Suspense boundary to ensure it streams correctly in SSR.
 */
function CategoryJsonLd({
    categorySchemaPromise,
}: {
    categorySchemaPromise: Promise<ReturnType<typeof generateCategorySchema> | null>;
}) {
    const categorySchema = use(categorySchemaPromise);
    const rootData = useRouteLoaderData<typeof rootLoader>('root');
    const nonce = rootData?.nonce ?? undefined;
    return categorySchema ? <JsonLd data={categorySchema} id="category-schema" nonce={nonce} /> : null;
}

export default function CategoryPage({
    loaderData: {
        category,
        searchResultCritical,
        searchResultNonCritical,
        page,
        categoryId,
        pageUrl,
        refine,
        locale,
        currency,
        initialFiltersOpen,
        categorySchema,
        view: initialView,
    },
}: {
    loaderData: CategoryPageData;
}) {
    const config = useConfig();
    const { t } = useTranslation('common');

    const [filtersOpen, toggleFiltersOpen] = useFiltersPanelState(initialFiltersOpen);
    /**
     * CRCB2B-348. `filtersOpen` is the shopper's EXPLICIT choice; `panelState` distinguishes
     * "no choice made" from it, and `filtersVisible` is what the panel actually resolves to once
     * the breakpoint has spoken. The Filters control reports the last of the three, because at
     * desktop with no parameter the panel is open while `filtersOpen` is still false — a control
     * announcing `aria-pressed={filtersOpen}` there would contradict what is on screen.
     */
    const panelState = usePanelState();
    const filtersVisible = useFiltersVisible(filtersOpen);
    /**
     * The Filters control toggles what the shopper can SEE, so it must key off `filtersVisible`,
     * not `filtersOpen`. At desktop with no parameter the sidebar is open while `filtersOpen` is
     * still false, so a plain toggle wrote `filters=open` over an already-open panel and the first
     * click did nothing.
     */
    const toggleFiltersVisible = useCallback(
        () => toggleFiltersOpen(!filtersVisible),
        [filtersVisible, toggleFiltersOpen]
    );
    // Seeded from the cookie the loader read, so the first paint already matches the shopper's
    // preference. Switching flips local state immediately and persists in the background — no
    // navigation, no URL change, and therefore no revalidation.
    const [view, selectView] = useProductListingView(initialView);
    const limit = config.search.products.hits.limit;

    // Determine the maximum number of skeletons to display in the product grid.
    // Out-of-the-box the idea is to not display more than 8 skeletons, i.e., two rows on a desktop device.
    // Wrap in Math.max(0, ...) to prevent negative values when criticalCount is high(er).
    const criticalCount = searchResultCritical.hits?.length ?? 0;
    const nonCriticalCount = Math.max(
        0,
        Math.min(8, limit, searchResultCritical.total - searchResultCritical.offset) - criticalCount
    );

    const analytics = useAnalytics();
    const lastTrackedDataRef = useRef<string | null>(null);

    const location = useLocation();
    const searchWithoutFiltersParam = useMemo(() => getSearchWithoutFiltersParam(location.search), [location.search]);
    const pageIdentity = `${categoryId}-${currency}-${locale}`;
    const analyticsKey = `${pageIdentity}-${searchWithoutFiltersParam}-${location.hash}`;
    const productGridDataKey = `${pageIdentity}-${searchWithoutFiltersParam}`;
    const selectedFiltersCount = useMemo(
        () => new URLSearchParams(location.search).getAll('refine').length,
        [location.search]
    );

    /**
     * The refinements the SHOPPER applied. NOT `refine.length` — that is the loader's
     * `effectiveRefine`, which always carries a synthetic `cgid=<categoryId>` entry, so it is
     * never zero and an empty category would read as an over-filtered one.
     */
    const shopperRefineCount = useMemo(() => refine.filter((r) => !r.startsWith('cgid=')).length, [refine]);

    /*
     * CRCB2B-87 extracted this from an inline `useMemo` into a shared hook; taking their version
     * rather than keeping the copy this branch started from.
     *
     * The `categoryLabel` / `showQuickFilters` block that arrived alongside it is deliberately NOT
     * carried over: CRCB2B-348 deleted QuickFilters and removed `uiConfig.pages.category`, having
     * settled the `TODO(CRCB2B-348)` those flags were waiting on — category selection is navigation
     * (nav menu and breadcrumbs), so there are neither chips nor a Category group in the rail.
     */
    const isProductGridLoading = useResultsGridLoading();

    const nonCriticalPromise = useMemo(
        () => searchResultNonCritical.then((r) => r.hits ?? []),
        [searchResultNonCritical]
    );

    const [, startTransition] = useTransition();

    useEffect(() => {
        // Only track if we haven't already tracked this specific data combination
        if (analyticsKey !== lastTrackedDataRef.current) {
            lastTrackedDataRef.current = analyticsKey;

            startTransition(() => {
                void nonCriticalPromise
                    .then((searchHitsData: ShopperSearch.schemas['ProductSearchHit'][]) => {
                        if (analytics) {
                            void analytics.trackViewCategory({
                                category,
                                searchResults: [...(searchResultCritical.hits ?? []), ...searchHitsData],
                                sort:
                                    searchResultCritical.selectedSortingOption ||
                                    searchResultCritical.sortingOptions?.[0]?.label ||
                                    '',
                                refinements: searchResultCritical.selectedRefinements ?? {},
                            });
                        }
                    })
                    .catch(() => {
                        // Silently handle promise rejection
                    });
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [analytics, category, analyticsKey, nonCriticalPromise]);

    const handleProductClick = useCallback(
        (product: ShopperSearch.schemas['ProductSearchHit']) => {
            if (analytics) {
                void analytics.trackClickProductInCategory({
                    category,
                    product,
                });
            }
        },
        [analytics, category]
    );

    return (
        <>
            <SeoMeta
                title={category.name || category.id}
                description={category.pageDescription || category.description}
                openGraph={{
                    type: 'website',
                    url: pageUrl,
                }}
            />
            <div className="pb-16 -mt-8">
                {/* plpTopFullWidth — full-width banner region, flush to the header (mirrors homepage pattern) */}
                <Region
                    page={page}
                    regionId="plpTopFullWidth"
                    fallbackElement={<CategoryBannerSkeleton />}
                    errorElement={<CategoryBanner />}
                    fallbackOnEmpty
                />

                {/* Page grid (CRCB2B-87). Figma desktop `1396:10895`, mobile `11546:79050`.
                    Replaces `section-container` + a nested flex row, which put the breadcrumbs
                    and title FULL WIDTH above the two columns. The design has the sidebar
                    starting at the very top of the content area, with breadcrumbs, title,
                    toolbar and results all in the right column beside it.

                    Measured from the 1440 frame: 32px page padding, a 32px gutter, sidebar
                    336px. `minmax(0,1fr) minmax(0,3fr)` reproduces 336/1008 exactly at that
                    width and stays proportional above it. `minmax(0,…)` rather than plain `fr`
                    so a long product name cannot force the column wider than its share.

                    NOT `section-container`: that resolves to `max-w-screen-2xl` (1536px) with
                    64px padding, and whether the Figma 1440/32 values should govern the whole
                    storefront is a site-wide question a previous change deliberately left open.
                    Scoped here per `theme/tailwind.css`, which names `max-w-[90rem]` (or a
                    scoped token) as the layout max-width "when layout work lands".

                    DOM order is the mobile order — title, toolbar, panel, results — so the
                    single-column fallback needs no reordering. Desktop placement lifts the panel
                    into column 1 spanning both rows. */}
                <div
                    // CRCB2B-348: the panel's open state is CSS-driven, not boolean-driven.
                    //
                    // The design shows the sidebar OPEN at desktop, and that default cannot be
                    // expressed in state — `initialFiltersOpen` is computed in the loader, where
                    // the viewport is unknown, so a breakpoint-aware JS default would mismatch on
                    // hydration or force a client-only render. So the query parameter grows a
                    // third state, `default` (absent), and the breakpoint resolves it here.
                    //
                    // This REPLACES three `filtersOpen &&` conditionals (this one plus the two
                    // `lg:col-start-2` placements below) with an always-two-column grid at `lg`
                    // whose first track collapses to zero when the shopper closes the panel. That
                    // is fewer moving parts, not more: the children's placement becomes
                    // unconditional, so nothing has to stay in sync with a boolean.
                    data-filters={panelState}
                    className={cn(
                        // Mobile: 16px padding and a 16px gap (`--layout-padding-*` / `--spacing-4`).
                        // Desktop: 32px padding, 32px column gutter, 24px row gap
                        // (`--spacing-8` / `--spacing-6`). No `sm:` step — the two designed
                        // frames are 375 and 1440, and inventing a tablet padding between them
                        // would be a value nobody specified.
                        'mx-auto grid w-full max-w-[90rem] grid-cols-1 gap-4 p-4 lg:gap-x-8 lg:gap-y-6 lg:p-8',
                        // Row 1 is the title block, sized to its content.
                        //
                        // The design fixes that row at 181px, and an earlier version honoured it
                        // as a floor via `minmax(181px, auto)`. That was wrong for what we
                        // actually render: grid items stretch, so the block filled the whole
                        // 181px and its bottom rule drifted away from the text above it. The
                        // design's 181px assumes breadcrumbs + heading + a "Page Description"
                        // line; we deliberately render the result count instead and no
                        // description, so the block is shorter and the floor showed up as dead
                        // space between the count and the rule.
                        //
                        // Sizing to content puts the rule back 16px under the count (`pb-4`).
                        // The sidebar is unaffected — it spans both rows, so it still starts at
                        // the top of row 1; only the toolbar moves up with it.
                        'lg:grid-cols-[minmax(0,1fr)_minmax(0,3fr)] lg:grid-rows-[auto_1fr]',
                        // Closed: the sidebar track collapses to nothing and its gutter goes with
                        // it, so the content column reclaims the full width WITHOUT the children
                        // needing to move. `col-start-2` in a zero-width first track is still
                        // column 2 — it just has nothing to its left.
                        'lg:data-[filters=closed]:grid-cols-[0_minmax(0,1fr)] lg:data-[filters=closed]:gap-x-0'
                    )}>
                    {/* Title block — column 2, row 1. The result count is its OWN line rather
                        than a parenthetical inside the heading (CRCB2B-87): the heading is the
                        category name, the count is a translated, pluralized string.
                        `category.pageDescription` deliberately stays in SeoMeta only — the
                        desktop frame shows a description slot, but the confirmed behaviour is
                        the mobile frame's count-only. */}
                    <div
                        className={cn(
                            // Spacing lives on this container as a `gap`, not as margins on the
                            // children — so the breadcrumbs get `mb-0` below. The bottom border
                            // and its 16px stand-off are the design's rule under the title block.
                            'flex flex-col items-start gap-4 border-b border-border pb-4 lg:mb-4',
                            // Unconditional (CRCB2B-348): the grid is always two-column at `lg`,
                            // and a closed panel collapses track 1 rather than restructuring.
                            'lg:col-start-2 lg:row-start-1'
                        )}>
                        <CategoryBreadcrumbs category={category} />
                        {/* Heading and count are grouped so they can sit 4px apart while the
                            breadcrumbs stay 16px away — one `gap` on the parent cannot express
                            two different spacings. */}
                        <div className="flex flex-col items-start lg:gap-1">
                            {/* Mobile heading/md: 40px at 110%, weight 450. Desktop heading/xl:
                                64px at 110%. Both line-heights are carried by the `text-*`
                                tokens, and both letter-spacings are 3% (1.2/40 and 1.92/64), so
                                `tracking-sram` serves both.

                                Deliberately NOT `font-bold`. The SRAM scale runs 45 Light (400) /
                                55 Roman (450) / medium (500). `--font-weight-heading-2` is 450,
                                which the theme documents as the `font-book` weight; heading-1 has
                                no token yet, and 400 is read off the desktop frame. */}
                            <h1 className="text-4xl font-book tracking-sram text-foreground lg:text-7xl lg:font-normal">
                                {category?.name || category.id}
                            </h1>
                            {/* body/default: 16px at 150% (both from `text-md`), 3% tracking,
                                text-color-secondary. The count is its own line rather than a
                                parenthetical in the heading (CRCB2B-87), and it is a translated,
                                pluralized string. `category.pageDescription` stays in SeoMeta
                                only — the desktop frame shows a description slot, but the
                                confirmed behaviour is the mobile frame's count-only. */}
                            <p
                                className="text-md font-book tracking-sram text-muted-foreground"
                                data-testid="category-result-count">
                                {t('productListing.results', { count: searchResultCritical.total })}
                            </p>
                        </div>
                        <UITarget targetId="sfcc.plp.search.summary" />
                    </div>

                    {/* Toolbar — mobile. A grid child in its own right, so on narrow widths it
                        falls between the title block and the refinement panel: the panel is
                        still inline in flow there, and a Filters button rendered *below* the
                        panel it opens reads backwards. `lg:hidden` keeps exactly one of the two
                        toolbars in the accessibility tree, which is also why these controls
                        carry no `data-testid` — query by role and accessible name.

                        When CRCB2B-348 turns the mobile panel into a drawer, this instance can
                        merge with the desktop one. */}
                    <div className="lg:hidden flex flex-col items-stretch gap-4" data-slot="filters-wrapper">
                        {/* Mobile row 1: sort pinned left, Filters pinned right.
                            `justify-between` splits them to their edges, and Filters ALSO carries
                            `ml-auto` because sort is conditional — the platform need not return
                            sorting options. With only one child `justify-between` places it at
                            the start, which would drop a lone Filters button on the left; the
                            auto margin is what holds it right, and it is inert when both render.

                            DOM order is the visual order on purpose — no `order-*`. Reordering
                            with flex would leave the tab sequence and the screen-reader reading
                            order as Filters-then-sort while the eye sees sort-then-Filters, which
                            is the mismatch WCAG 1.3.2 (Meaningful Sequence) is about. Swapping the
                            JSX costs nothing here because neither control is positioned by the
                            grid. */}
                        <div className="flex items-center justify-between gap-2">
                            {searchResultCritical?.sortingOptions && searchResultCritical.sortingOptions.length > 0 && (
                                <CategorySorting result={searchResultCritical} />
                            )}
                            <FiltersButton
                                onClick={toggleFiltersVisible}
                                isActive={filtersVisible}
                                panelState={panelState}
                                selectedFiltersCount={selectedFiltersCount}
                                className="ml-auto"
                            />
                        </div>
                        {/* Mobile row 2: the view toggle on its own line, right-aligned. On
                            desktop all three share one row — see the desktop toolbar below. */}
                        <CategoryViewToggle value={view} onValueChange={selectView} className="self-end" />
                    </div>

                    {/* Refinement panel — column 1, spanning BOTH rows, so it starts level with
                        the breadcrumbs as the design shows. It must be a direct child of the page
                        grid for that placement to be expressible at all; nesting it inside the
                        content column (as this route used to) is what forced the title full-width
                        above both columns. No explicit width — the 336px comes from the `1fr` of
                        the 1fr/3fr track pair. */}
                    <CategoryRefinementsPanel
                        result={searchResultCritical}
                        refine={refine}
                        filtersOpen={filtersOpen}
                        onFiltersOpenChange={toggleFiltersOpen}
                        className="w-full lg:col-start-1 lg:row-start-1 lg:row-span-2"
                    />

                    <div className="lg:col-start-2 lg:row-start-2">
                        {/* Toolbar — desktop. One row below the title block: sort on the left
                                edge, then Filters and the view toggle paired on the right.

                                The split is a single `ml-auto` on Filters, not a wrapper div —
                                an auto margin absorbs the free space at that point in the line,
                                so Filters and everything after it move right together while the
                                row's own `gap-4` still sets the space between them. A wrapper
                                would have to restate that gap and would nest the two controls
                                for no semantic reason.

                                As on mobile, the visual order is the DOM order — no `order-*`,
                                which would desynchronise the tab sequence from what is seen
                                (WCAG 1.3.2). */}
                        <div className="mb-4 hidden lg:flex lg:items-center lg:gap-4" data-slot="filters-wrapper">
                            {searchResultCritical?.sortingOptions && searchResultCritical.sortingOptions.length > 0 && (
                                <CategorySorting result={searchResultCritical} />
                            )}
                            <FiltersButton
                                onClick={toggleFiltersVisible}
                                isActive={filtersVisible}
                                panelState={panelState}
                                selectedFiltersCount={selectedFiltersCount}
                                className="ml-auto"
                            />
                            <CategoryViewToggle value={view} onValueChange={selectView} />
                        </div>

                        <ActiveFilters result={searchResultCritical} />

                        {/* CRCB2B-348. Filters that exclude everything are a different situation
                            from an empty category, with a different remedy, so they get a
                            different message. Rendering it here rather than inside the grid keeps
                            it beside the chips that caused it — and `suppressEmptyMessage` stops
                            the grid's own "no products found" stacking underneath. */}
                        <InvalidFilterCombination total={searchResultCritical.total} refineCount={shopperRefineCount} />

                        {/* plpTopContent */}
                        <Region className="mb-8" page={page} regionId="plpTopContent" />

                        <UITarget targetId="sfcc.plp.agent.categoryHelper" />
                        <UITarget targetId="sfcc.plp.search.results">
                            <DeferredProductGrid
                                key={productGridDataKey}
                                critical={searchResultCritical.hits ?? []}
                                nonCritical={nonCriticalPromise}
                                nonCriticalCount={nonCriticalCount}
                                hasRefinementsPanel={filtersVisible}
                                // CRCB2B-348: when filters excluded everything, the message above
                                // explains it. Without this the grid stacks a second, vaguer one.
                                // The predicate comes FROM that message's module, so the two can
                                // only ever be exact complements.
                                suppressEmptyMessage={isOverFiltered(searchResultCritical.total, shopperRefineCount)}
                                view={view}
                                isLoading={isProductGridLoading}
                                handleProductClick={handleProductClick}
                                errorElement={<ProductGridError />}
                            />
                        </UITarget>

                        {searchResultCritical.total > 1 && (
                            <div className="mt-10">
                                <CategoryPagination
                                    limit={limit}
                                    offset={searchResultCritical.offset}
                                    total={searchResultCritical.total}
                                />
                            </div>
                        )}

                        {/* plpBottom */}
                        <Region className="mt-8" page={page} regionId="plpBottom" />
                    </div>
                </div>
            </div>
            <Suspense fallback={null}>
                <CategoryJsonLd categorySchemaPromise={categorySchema} />
            </Suspense>
        </>
    );
}
